/**
 * Shared chart lifecycle utilities for Highcharts chart components.
 *
 * Provides a factory that returns bound helpers (destroy, visibility checks,
 * reflow, tab observer, auto-init) scoped to a per-chart-type Map of instances.
 */

import type Highcharts from "highcharts";

/** Renders (or re-renders) a chart into the given container element. */
type RenderFn = (container: HTMLElement) => void;
type InitFn = (root?: ParentNode) => void;
type ChartWithRenderTarget = Highcharts.Chart & { renderTo: HTMLElement };

export interface ChartLifecycleOptions {
  deferUntilIntersect?: boolean;
  rootMargin?: string;
}

export interface ChartLifecycleReflowOptions {
  forceRender?: boolean;
}

export interface ChartLifecycle {
  instances: Map<string, Highcharts.Chart>;
  destroyChart(containerId: string): void;
  isContainerVisible(container: HTMLElement): boolean;
  getContainers(root?: ParentNode): HTMLElement[];
  pruneStaleCharts(activeIds: Set<string>, root?: ParentNode): void;
  reflowChartForContainer(container: HTMLElement, options?: ChartLifecycleReflowOptions): void;
  reflowChartsIn(targetEl: Element | null): void;
  scheduleVisibleReflow(): void;
  setupTabReflowObserver(): void;
  setupAutoInit(initFn: InitFn): void;
}

/**
 * Creates a chart lifecycle helper scoped to a specific chart selector.
 *
 * Each call returns an independent set of helpers with its own instances map,
 * tab observer flag, and auto-init guard.
 *
 * @param selector - CSS selector for chart containers (e.g. '.scenario-projection-chart').
 * @param renderFn - Function that renders a chart into a container element.
 *                    Called for lazy-rendering when a hidden container becomes visible.
 * @returns A ChartLifecycle object with bound helper functions.
 */
export function createChartLifecycle(
  selector: string,
  renderFn: RenderFn,
  options: ChartLifecycleOptions = {},
): ChartLifecycle {
  const instances: Map<string, Highcharts.Chart> = new Map();
  const observedContainers: WeakSet<HTMLElement> = new WeakSet();
  let tabObserverInitialized = false;
  let autoInitRegistered = false;
  let intersectionObserver: IntersectionObserver | null = null;

  function destroyChart(containerId: string): void {
    const chart = instances.get(containerId);
    if (!chart) return;
    chart.destroy();
    instances.delete(containerId);
  }

  function isContainerVisible(container: HTMLElement): boolean {
    if (container.closest(".hidden") || container.closest("[hidden]")) {
      return false;
    }
    return container.getClientRects().length > 0;
  }

  function getContainers(root: ParentNode = document): HTMLElement[] {
    const containers: HTMLElement[] = [];
    if (root instanceof HTMLElement && root.matches(selector)) {
      containers.push(root);
    }
    root.querySelectorAll<HTMLElement>(selector).forEach((container) => {
      if (!containers.includes(container)) {
        containers.push(container);
      }
    });
    return containers;
  }

  function getIntersectionObserver(): IntersectionObserver | null {
    if (!options.deferUntilIntersect || typeof IntersectionObserver === "undefined") {
      return null;
    }
    if (intersectionObserver) {
      return intersectionObserver;
    }
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const container = entry.target as HTMLElement;
          if (!entry.isIntersecting) continue;
          if (!isContainerVisible(container)) continue;
          intersectionObserver?.unobserve(container);
          observedContainers.delete(container);
          renderFn(container);
        }
      },
      {
        rootMargin: options.rootMargin ?? "200px 0px",
      },
    );
    return intersectionObserver;
  }

  function observeForDeferredRender(container: HTMLElement): boolean {
    const observer = getIntersectionObserver();
    if (!observer) {
      return false;
    }
    if (!observedContainers.has(container)) {
      observedContainers.add(container);
      observer.observe(container);
    }
    return true;
  }

  function chartBelongsToContainer(chart: Highcharts.Chart, container: HTMLElement): boolean {
    return (chart as ChartWithRenderTarget).renderTo === container;
  }

  function reflowChartForContainer(
    container: HTMLElement,
    options: ChartLifecycleReflowOptions = {},
  ): void {
    const chart = instances.get(container.id);
    let shouldDeferRender = !chart;
    if (chart) {
      if (!chartBelongsToContainer(chart, container) || options.forceRender) {
        destroyChart(container.id);
        shouldDeferRender = !options.forceRender;
      } else {
        if (isContainerVisible(container) && typeof chart.reflow === "function") {
          chart.reflow();
        }
        return;
      }
    }

    if (isContainerVisible(container)) {
      if (shouldDeferRender && observeForDeferredRender(container)) {
        return;
      }
      renderFn(container);
    }
  }

  function reflowChartsIn(targetEl: Element | null): void {
    if (!targetEl) return;
    getContainers(targetEl).forEach((container) => {
      reflowChartForContainer(container);
    });
  }

  function pruneStaleCharts(activeIds: Set<string>, root: ParentNode = document): void {
    const isDocumentScope = root === document;
    for (const [containerId, chart] of instances) {
      const container = document.getElementById(containerId);
      if (!container || !chartBelongsToContainer(chart, container)) {
        destroyChart(containerId);
        continue;
      }
      const isInScope = isDocumentScope || (root instanceof Element && root.contains(container));
      if (isInScope && !activeIds.has(containerId)) {
        destroyChart(containerId);
      }
    }
  }

  function scheduleVisibleReflow(): void {
    if (instances.size === 0) return;

    window.setTimeout(() => {
      instances.forEach((chart, containerId) => {
        const container = document.getElementById(containerId);
        if (!container || !chartBelongsToContainer(chart, container)) return;
        if (!isContainerVisible(container)) return;
        if (typeof chart.reflow === "function") {
          chart.reflow();
        }
      });
    }, 0);
  }

  function setupTabReflowObserver(): void {
    if (tabObserverInitialized) return;
    tabObserverInitialized = true;

    document.addEventListener("click", (event) => {
      const target = event.target as Element | null;
      const tabButton = target?.closest('[role="tab"][data-tabs-target]');
      if (!tabButton) return;

      const targetSelector = tabButton.getAttribute("data-tabs-target");
      if (!targetSelector) return;

      window.setTimeout(() => {
        const targetEl = document.querySelector(targetSelector);
        reflowChartsIn(targetEl);
      }, 0);
    });
  }

  function resolveCustomReflowTarget(event: Event): Element | null {
    const customEvent = event as CustomEvent<{ targetId?: string; targetSelector?: string }>;
    const targetId = customEvent.detail?.targetId;
    if (targetId) {
      return document.getElementById(targetId);
    }

    const targetSelector = customEvent.detail?.targetSelector;
    if (targetSelector) {
      return document.querySelector(targetSelector);
    }

    return event.target instanceof Element ? event.target : null;
  }

  function setupAutoInit(initFn: InitFn): void {
    if (autoInitRegistered || typeof document === "undefined") return;
    autoInitRegistered = true;

    const initialize = (event?: Event) => {
      const root = event?.target instanceof Element ? event.target : document;
      initFn(root);
      scheduleVisibleReflow();
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initialize);
    } else {
      initialize();
    }

    document.addEventListener("htmx:afterSettle", initialize);
    document.addEventListener("sam:charts:reflow", (event) => {
      reflowChartsIn(resolveCustomReflowTarget(event));
    });
    setupTabReflowObserver();
  }

  return {
    instances,
    destroyChart,
    isContainerVisible,
    getContainers,
    pruneStaleCharts,
    reflowChartForContainer,
    reflowChartsIn,
    scheduleVisibleReflow,
    setupTabReflowObserver,
    setupAutoInit,
  };
}
